import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
} from "@chakra-ui/react";
import SettingsDescription from "../elements/SettingsDescription";
import SettingsHeader from "../elements/SettingsHeader";

import { useContext } from "react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChatContext } from "providers/chat";
import { AlertsContext } from "providers/alerts";
import UserAvatar from "components/elements/UserAvatar";
import { getCustomButtonVariant } from "utils/uiconfighelper";
import { getErrorMessage } from "utils/errorHandler";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useAuthenticationActions } from "modules/authentication/mutations/authentication-actions";
import { useUpdateUser } from "modules/tenant/mutations/use-update-user";
import { useUploadAvatar } from "modules/tenant/mutations/use-upload-avatar";

export default function AccountSettings() {
  const { user } = useTenant();
  const { chatClient } = useContext(ChatContext);
  const { showError, showSuccess } = useContext(AlertsContext);
  const { signOut } = useAuthenticationActions();

  const [firstName, setFirstName] = useState(user.first_name);
  const [lastName, setLastName] = useState(user.last_name);
  const [email, setEmail] = useState(user.email);

  const updateUser = useUpdateUser();
  const uploadAvatar = useUploadAvatar();

  const logout = async () => {
    await chatClient?.shutdown();
    await signOut.mutateAsync();
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles[0]) return;

      try {
        await uploadAvatar.mutateAsync({
          image: acceptedFiles[0],
          userId: user.id,
        });
        showSuccess({
          title: "Avatar Updated",
          description: "Your profile picture has been successfully updated.",
        });
      } catch (err) {
        console.error(err);
        showError({
          title: "Upload Failed",
          description: getErrorMessage(err),
        });
      }
    },
    [user.id, uploadAvatar, showSuccess, showError]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "image/*": [".png", ".jpeg", ".jpg"],
    },
  });

  const handleUpdateUser = async () => {
    try {
      await updateUser.mutateAsync({
        email,
        first_name: firstName,
        last_name: lastName,
      });
      showSuccess({
        title: "Account Updated",
        description: "Your account information has been successfully updated.",
      });
    } catch (err) {
      console.error(err);
      showError({
        title: "Save Failed",
        description: getErrorMessage(err),
      });
    }
  };

  return (
    <Box>
      <SettingsHeader title="Account Information" />
      <SettingsDescription text="Update your profile details here" />
      <HStack spacing="40px" alignItems="top">
        <Box flex="4">
          <HStack spacing="40px">
            <Box flex="1">
              <FormControl mb="16px">
                <FormLabel>First Name</FormLabel>
                <Input
                  variant="primary"
                  value={firstName}
                  onChange={(ev: any) => setFirstName(ev.target.value)}
                />
              </FormControl>
            </Box>
            <Box flex="1">
              <FormControl mb="16px">
                <FormLabel>Last Name</FormLabel>
                <Input
                  variant="primary"
                  value={lastName}
                  onChange={(ev: any) => setLastName(ev.target.value)}
                />
              </FormControl>
            </Box>
          </HStack>
          <FormControl mb="16px">
            <FormLabel>Email Address</FormLabel>
            <Input
              disabled
              variant="primary"
              value={email}
              onChange={(ev: any) => setEmail(ev.target.value)}
            />
          </FormControl>
          <Divider mt="20px" mb="20px" />
          <Flex alignItems="center" justifyContent="space-between">
            <Button data-cy="log-out" variant="secondary" onClick={logout}>
              Log Out
            </Button>
            <Button
              variant={getCustomButtonVariant(user)}
              isLoading={updateUser.isPending}
              onClick={handleUpdateUser}
            >
              Save Changes
            </Button>
          </Flex>
        </Box>
        <Box flex="3">
          <FormControl mb="20px">
            <FormLabel>Avatar</FormLabel>
            <Flex>
              <UserAvatar size="74px" mr="18px" user={user} hideOnline />
              <Box>
                <Text color="gray.200" fontSize="14px" mb="4px">
                  We recommend using a square headshot for your avatar.
                </Text>
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Button
                    isDisabled={uploadAvatar.isPending}
                    variant="secondary"
                    size="sm"
                    onClick={open}
                    leftIcon={<FontAwesomeIcon icon={["far", "upload"]} />}
                  >
                    {isDragActive ? "Drop" : "Upload"} Picture
                  </Button>
                </div>
              </Box>
            </Flex>
          </FormControl>
        </Box>
      </HStack>
    </Box>
  );
}
